/********************************************************************
 * ADOBE CONFIDENTIAL
 * __________________
 *
 *  Copyright 2024 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 *******************************************************************/

import { FunctionComponent } from 'preact';
import { Button, Modal, ProgressSpinner } from '@adobe-commerce/elsie/components';
import { useText } from '@adobe-commerce/elsie/i18n';
import AddressCard from '@/account/components/AddressCard';
import '@/account/components/AddressModal/AddressModal.css';
import { AddressModalProps } from '@/account/types';

export const AddressModal: FunctionComponent<AddressModalProps> = ({
  minifiedView,
  keysSortOrder,
  addressData,
  open,
  submitLoading,
  onRemoveAddress,
  closeModal,
}) => {
  const minifiedViewKey = minifiedView ? 'minifiedView' : 'fullSizeView';

  const translations = useText({
    title: `Account.${minifiedViewKey}.Addresses.removeAddressModal.title`,
    description: `Account.${minifiedViewKey}.Addresses.removeAddressModal.description`,
    actionCancel: `Account.${minifiedViewKey}.Addresses.removeAddressModal.actionCancel`,
    actionConfirm: `Account.${minifiedViewKey}.Addresses.removeAddressModal.actionConfirm`,
  });

  if (!open) return null;

  return (
    <Modal
      className="account-address-modal--overlay"
      title={<h3>{translations.title}</h3>}
      size="full"
      data-testid="addressModal"
      showCloseButton
      onClose={closeModal}
    >
      <div className="account-address-modal">
        {submitLoading ? (
          <div
            className="account-address-modal__spinner"
            data-testid="progressSpinner"
          >
            <ProgressSpinner stroke={'4'} size={'large'} />
          </div>
        ) : null}
        <p>{translations.description}</p>
        <AddressCard
          minifiedView={minifiedView}
          addressData={addressData}
          keysSortOrder={keysSortOrder}
        />
        <div className="account-address-modal__buttons">
          <Button
            // @ts-ignore
            type={'button'}
            onClick={closeModal}
            variant="secondary"
            disabled={submitLoading}
          >
            {translations.actionCancel}
          </Button>
          <Button disabled={submitLoading} onClick={onRemoveAddress}>
            {translations.actionConfirm}
          </Button>
        </div>
      </div>
    </Modal>
  );
};
